import React, { useEffect, useState, useCallback } from 'react';
import centralBank from './assets/logos/central-bank-libya.png';

const UsdRateDisplay = ({ isArabic }) => {
  const [data, setData] = useState(() => {
    // Try to load cached data from localStorage
    const cached = localStorage.getItem('usd-rate-cache');
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        const cacheAge = Date.now() - parsed.timestamp;
        // Use cache if less than 30 minutes old
        if (cacheAge < 30 * 60 * 1000) {
          return parsed.data;
        }
      } catch (e) {
        localStorage.removeItem('usd-rate-cache');
      }
    }
    return null;
  });
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  const text = {
    en: {
      currentRate: "Current Exchange Rate",
      fromCBL: "From Central Bank of Libya",
      buyingRate: "Buying Rate",
      sellingRate: "Selling Rate",
      lastUpdated: "Last Updated",
      loading: "Loading rates...",
      unavailable: "Currently unavailable",
      errorMessage: "Unable to load exchange rate"
    },
    ar: {
      currentRate: "سعر الصرف الحالي",
      fromCBL: "من مصرف ليبيا المركزي",
      buyingRate: "سعر الشراء",
      sellingRate: "سعر البيع",
      lastUpdated: "آخر تحديث",
      loading: "جاري تحميل الأسعار...",
      unavailable: "غير متاح حالياً",
      errorMessage: "تعذر تحميل سعر الصرف"
    }
  };

  const fetchUsdRate = useCallback(async () => {
    try {
      setLoading(true);
      setError(false);
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10s timeout
      
      const response = await fetch('https://cbl-proxy.alharethalalem.workers.dev/', {
        signal: controller.signal,
        headers: {
          'Accept': 'application/json',
          'Cache-Control': 'no-cache'
        }
      });
      
      clearTimeout(timeoutId);
      
      if (response.ok) {
        const apiData = await response.json();
        
        if (apiData.status === 'success' && apiData.data && apiData.data.length > 0) {
          const usdData = apiData.data[0];
          
          const buyRate = parseFloat(usdData.buy.replace(/\.$/, '')) || 0;
          const sellRate = parseFloat(usdData.sell.replace(/\.$/, '')) || 0;
          const avgRate = parseFloat(usdData.avg.replace(/\.$/, '')) || 0;
          
          console.log('USD Data:', usdData);
          console.log('Parsed rates:', { buyRate, sellRate, avgRate });
          
          if (buyRate > 0 && sellRate > 0) {
            const newData = {
              buy: buyRate,
              sell: sellRate,
              average: avgRate || (buyRate + sellRate) / 2,
              updated_at: usdData.date,
              source: "Central Bank of Libya"
            };
            
            setData(newData);
            
            // Cache the data
            localStorage.setItem('usd-rate-cache', JSON.stringify({
              data: newData,
              timestamp: Date.now()
            }));
            
            setLoading(false);
            return;
          }
        }
      }
      
      throw new Error('Invalid API response');
    } catch (err) {
      if (err.name === 'AbortError') {
        console.log('Request timed out');
      } else {
        console.error('Error fetching USD rate:', err);
      }
      setError(true);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsdRate();
    // Refresh every hour (3600000 ms)
    const interval = setInterval(fetchUsdRate, 3600000);
    
    return () => clearInterval(interval);
  }, []);

  const currentLang = isArabic ? 'ar' : 'en';

  return (
    <div className="bg-white rounded-lg sm:rounded-xl shadow-2xl p-3 sm:p-4 w-full max-w-sm border-2 sm:border-4" style={{borderColor: '#006655'}}>
      <div className="space-y-2 sm:space-y-3">
        {/* Header */}
        <div className="text-center">
          <h3 className="text-sm sm:text-base font-semibold mb-0.5" style={{color: '#013220'}}>
            {text[currentLang].currentRate}
          </h3>
          <p className="text-xs text-gray-600">
            {text[currentLang].fromCBL}
          </p>
        </div>

        {/* Central Bank Logo */}
        <div className="flex justify-center">
          <div className="w-32 h-24 sm:w-36 sm:h-28 flex items-center justify-center p-1">
            <img src={centralBank} alt="Central Bank of Libya" className="max-w-full max-h-full object-contain" fetchPriority="high" />
          </div>
        </div>

        {/* Main Exchange Rate Display */}
        {loading ? (
          <div className="text-center py-4 sm:py-6">
            <div className="animate-spin rounded-full h-5 w-5 sm:h-6 sm:w-6 border-b-2 border-gray-900 mx-auto mb-2"></div>
            <p className="text-xs text-gray-500">{text[currentLang].loading}</p>
          </div>
        ) : error ? (
          <div className="text-center py-4 sm:py-6 text-gray-500 text-xs sm:text-sm">
            <p>{text[currentLang].errorMessage}</p>
          </div>
        ) : data ? (
          <>
            {/* Main Rate Display */}
            <div className="rounded-lg p-2 sm:p-3 text-center border-2" style={{backgroundColor: '#F4F7FA', borderColor: '#006655'}}>
              <div className="text-xs text-gray-600 mb-1">USD to LYD</div>
              <div className="text-xl sm:text-2xl font-bold mb-1" style={{color: '#013220'}}>
                1 USD = {data.average.toFixed(4)} LYD
              </div>
              <div className="mt-1 pt-1 border-t border-gray-300">
                <div className="text-xs font-medium text-gray-700 mb-0.5">
                  {text[currentLang].lastUpdated}
                </div>
                <div className="text-xs sm:text-sm font-semibold" style={{color: '#006655'}}>
                  {data.updated_at ? (() => {
                    if (currentLang === 'ar') {
                      const months = ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'];
                      const [year, month, day] = data.updated_at.split('-');
                      const monthName = months[parseInt(month) - 1];
                      return `${year} ${monthName} ${day}`;
                    }
                    const [year, month, day] = data.updated_at.split('-');
                    const date = new Date(year, month - 1, day);
                    return date.toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    }).replace(/(\w+) (\d+), (\d+)/, '$2 $1 $3');
                  })() : ''}
                </div>
              </div>
            </div>

            {/* Buying and Selling Rates */}
            <div className="grid grid-cols-2 gap-2 sm:gap-3" dir={isArabic ? 'rtl' : 'ltr'}>
              <div className="bg-gray-50 rounded-lg p-2 sm:p-3 text-center">
                <div className="text-xs text-gray-500 mb-1">{text[currentLang].buyingRate}</div>
                <div className="text-lg sm:text-xl font-bold text-gray-800">{data.buy.toFixed(4)}</div>
                <div className="text-xs text-gray-500">LYD</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-2 sm:p-3 text-center">
                <div className="text-xs text-gray-500 mb-1">{text[currentLang].sellingRate}</div>
                <div className="text-lg sm:text-xl font-bold text-gray-800">{data.sell.toFixed(4)}</div>
                <div className="text-xs text-gray-500">LYD</div>
              </div>
            </div>

            {/* Info Note */}
            <div className="bg-blue-50 rounded-lg p-2 sm:p-2.5 flex items-start" dir={isArabic ? 'rtl' : 'ltr'}>
              <svg className={`w-3 h-3 sm:w-4 sm:h-4 text-blue-600 ${isArabic ? 'ml-1 sm:ml-1.5' : 'mr-1 sm:mr-1.5'} mt-0.5 flex-shrink-0`} fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <p className={`text-xs text-gray-700 leading-relaxed ${isArabic ? 'text-right' : 'text-left'}`}>
                {isArabic 
                  ? 'هذه الأسعار من مصرف ليبيا المركزي وتُحدّث يومياً. الأسعار المعروضة هي للاسترشاد فقط.'
                  : 'These rates are from the Central Bank of Libya and are updated daily. Rates shown are for reference only.'}
              </p>
            </div>
          </>
        ) : (
          <div className="text-center py-4 sm:py-6 text-gray-500 text-xs sm:text-sm">
            {text[currentLang].unavailable}
          </div>
        )}
      </div>
    </div>
  );
};

export default UsdRateDisplay;